import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store,
  Palette,
  Puzzle,
  FileText,
  ShoppingBag,
  CreditCard,
  Pencil,
  Rocket,
  Info,
  Eye,
  EyeOff,
  CheckCircle2,
  GraduationCap,
  Copy,
  Bot,
  Truck,
  Headset,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import { useAuthStore } from '@/stores/auth-store';
import type { WizardState } from './useWizardState';

interface StepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}

const COURSE_REGEX = /course|lesson|training|education|learning/i;

function SummaryCard({
  icon: Icon,
  title,
  children,
  onEdit,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  onEdit?: () => void;
}) {
  return (
    <div className="border border-stone-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Icon className="w-4 h-4 text-text-secondary" />
          {title}
        </h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 font-medium transition-colors"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
        )}
      </div>
      <div className="space-y-1.5 text-sm text-text-secondary">{children}</div>
    </div>
  );
}

function ColorSwatch({ color, label }: { color: string; label?: string }) {
  return (
    <div className="flex items-center gap-1.5" title={label || color}>
      <div
        className="w-5 h-5 rounded border border-stone-200"
        style={{ backgroundColor: color }}
      />
      {label && <span className="text-xs text-text-muted">{label}</span>}
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="block text-xs font-medium text-text-secondary mb-1">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-stone-300 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function WizardStep6Review({ state, updateState }: StepProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const isCourse = COURSE_REGEX.test(state.businessDescription);
  const isDropship = !!state.features?.dropshipping;
  const isSaas = state.siteType === 'saas';
  const isMuseum = state.siteType === 'museum';
  const isLodging = state.siteType === 'lodging';
  const hasProducts = !isSaas && !isMuseum && !isLodging;

  // Count enabled features
  const enabledFeatures = Object.entries(state.features || {}).filter(([, v]) => v);
  const topFeatureKeys = enabledFeatures.slice(0, 5).map(([k]) => k);
  const featureLabels: Record<string, string> = {
    gift_cards: 'Gift Cards',
    loyalty_program: 'Loyalty Program',
    referral_program: 'Referral Program',
    reviews: 'Product Reviews',
    wishlist: 'Wishlist',
    newsletter: 'Newsletter',
    ai_features: 'AI Features',
    meilisearch: 'Full-text Search',
    courses: 'Courses',
    social_proof: 'Social Proof',
    abandoned_cart_recovery: 'Cart Recovery',
    back_in_stock: 'Back-in-Stock Alerts',
    product_bundles: 'Product Bundles',
    volume_discounts: 'Volume Discounts',
    countdown_timers: 'Countdown Timers',
  };

  // Extract palette colors for preview
  // Palettes are stored flat (e.g. { primary: { 500: '#...' } }) not nested under .light
  const primaryShades: string[] = [];
  const primaryPalette = state.palettes?.light?.primary || state.palettes?.primary;
  if (primaryPalette) {
    [50, 100, 300, 500, 700].forEach((shade) => {
      if (primaryPalette[shade]) primaryShades.push(primaryPalette[shade]);
    });
  }
  const accentColor = state.palettes?.light?.accent?.[500] || state.palettes?.accent?.[500] || state.palettes?.accent?.base || '';

  // Parse courses count
  let courseCount = 0;
  if (state.courseDescriptions) {
    try {
      courseCount = JSON.parse(state.courseDescriptions).length;
    } catch {
      // ignore
    }
  }

  async function handleCreateSite() {
    setError('');

    // Validate required fields
    if (!state.siteId || !state.storeName || !state.shortName) {
      setError('Site identity is incomplete. Please go back to Step 1.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Nav defaults differ by site type. Ecommerce = Shop/Blog/About/Help.
      // SaaS = Features/Pricing/Customers/Blog/Help (no shop, no cart).
      const saasHeaderNav = [
        { label: 'Features', href: '/features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Customers', href: '/case-studies' },
        { label: 'Blog', href: '/blog' },
        { label: 'Help', axissOpen: true },
      ];
      const ecomHeaderNav = [
        { label: 'Shop', href: '/shop' },
        { label: 'Blog', href: '/blog' },
        { label: 'About', href: '/page/about' },
        { label: 'Help', axissOpen: true },
      ];
      const saasFooterShop = [
        { label: 'Features', href: '/features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Case Studies', href: '/case-studies' },
        { label: 'Blog', href: '/blog' },
      ];
      const ecomFooterShop = [
        { label: 'All Products', href: '/shop' },
        { label: 'New Arrivals', href: '/shop?sort=newest' },
        { label: 'Blog', href: '/blog' },
      ];
      const saasFooterSupport = [
        { label: 'About', href: '/about' },
        { label: 'Contact', axissOpen: true, axissView: 'chat' },
        { label: 'Sign In', href: '/login' },
        { label: 'Get Started', href: '/signup' },
      ];
      const ecomFooterSupport = [
        { label: 'My Account', href: '/account' },
        { label: 'About Us', href: '/page/about' },
        { label: 'Live Chat', axissOpen: true },
      ];
      // Lodging (STR) nav. The wizard backend also injects this server-side
      // (the authoritative source for every generation path); mirrored here so
      // the Step-6 payload is honest and consistent for lodging sites.
      const lodgingHeaderNav = [
        { label: 'Stays', href: '/properties' },
        { label: 'Local Guide', href: '/page/things-to-do' },
        { label: 'About the Area', href: '/page/area-guide' },
        { label: 'House Rules', href: '/page/house-rules' },
        { label: 'Contact', href: '/page/contact' },
      ];
      const lodgingFooterBook = [
        { label: 'All Properties', href: '/properties' },
        { label: 'Check Availability', href: '/properties' },
        { label: 'My Trips', href: '/account/trips' },
        { label: 'Find My Reservation', href: '/reservations' },
      ];
      const lodgingFooterStay = [
        { label: 'House Rules', href: '/page/house-rules' },
        { label: 'Cancellation Policy', href: '/page/cancellation' },
        { label: 'Welcome Guide', href: '/page/welcome' },
        { label: 'Contact Host', href: '/page/contact' },
      ];

      const body = {
        siteId: state.siteId,
        siteType: state.siteType || 'ecommerce',
        ownership: state.ownership || 'platform',
        storeName: state.storeName,
        shortName: state.shortName,
        tagline: state.tagline || `Shop ${state.storeName}`,
        productTypeDescription: state.businessDescription,
        frontendDomain: state.frontendDomain || `${state.siteId}.rumio.world`,
        apiDomain: state.apiDomain || `api.${state.siteId}.rumio.world`,
        fontPairingId: state.fontPairingId,
        palettes: state.palettes,
        darkOverrides: state.darkOverrides,
        // Optional admin pick from Step 2; if empty the wizard backend lets
        // AI choose a Lucide icon name from sites/lucide-icon-catalog.ts.
        brandSymbol: state.brandSymbol || undefined,
        features: state.features,
        heroTitle: state.heroTitle,
        heroSubtitle: state.heroSubtitle,
        heroCTA: state.heroCTA,
        aboutMission: state.aboutMission,
        aboutDifferentiator: state.aboutDifferentiator,
        aboutAudience: state.aboutAudience,
        valuePropositions: state.valuePropositions,
        contactEmail: state.contactEmail,
        socialLinks: state.socialLinks,
        courseDescriptions: state.courseDescriptions || undefined,
        aliasDomains: state.aliasDomains?.length ? state.aliasDomains : undefined,
        productCount: state.productCount,
        blogPostCount: state.blogPostCount,
        generateContent: state.generateContent,
        stripeSecretKey: state.stripeSecretKey || undefined,
        stripePublishableKey: state.stripePublishableKey || undefined,
        stripeWebhookSecret: state.stripeWebhookSecret || undefined,
        setupAxiss: state.setupAxiss,
        // Voice only travels when Axiss does — the backend nests the step the
        // same way, and sending it alone would be a flag with no step.
        setupVoice: state.setupAxiss && state.setupVoice,
        voiceNumberType: state.voiceNumberType,
        voiceAreaCode: state.voiceAreaCode || undefined,
        setupTaxEngine: state.setupTaxEngine,
        // Museum-specific (only used when siteType === 'museum')
        museumHasGiftShop: state.museumHasGiftShop,
        museumCollectionFocus: state.museumCollectionFocus,
        museumTaxStatus: state.museumTaxStatus,
        museumCity: state.museumCity,
        museumRegion: state.museumRegion,
        // Lodging-specific (only used when siteType === 'lodging')
        lodgingMarketRegion: state.lodgingMarketRegion,
        lodgingHostType: state.lodgingHostType,
        lodgingPropertyCount: state.lodgingPropertyCount,
        lodgingPaymentModel: state.lodgingPaymentModel,
        lodgingSingleProperty: state.lodgingSingleProperty,
        headerNav: isSaas ? saasHeaderNav : isLodging ? lodgingHeaderNav : ecomHeaderNav,
        footerShopLinks: isSaas ? saasFooterShop : isLodging ? lodgingFooterBook : ecomFooterShop,
        footerSupportLinks: isSaas ? saasFooterSupport : isLodging ? lodgingFooterStay : ecomFooterSupport,
        payments: {
          stripe: !!state.stripeSecretKey,
          paypal: false,
        },
        adminEmail: user?.email || undefined,
        additionalAdmins: (state.additionalAdmins || []).filter((e: string) => e && e.includes('@')),
      };

      const result = await api.post<{ jobId: string }>(
        '/admin/platform/site-wizard/create',
        body,
      );
      navigate(`/admin/site-wizard/progress/${result.jobId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to start site creation');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="space-y-8">
      {/* Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Identity */}
        <SummaryCard
          icon={Store}
          title="Identity"
          onEdit={() => updateState({ currentStep: 0 })}
        >
          <p>
            <span className="font-medium text-text-primary">{state.storeName}</span>
          </p>
          <p>
            Type:{' '}
            <span className="font-medium text-text-primary">
              {state.siteType === 'saas'
                ? 'SaaS marketing site'
                : state.siteType === 'museum'
                ? `Museum / nonprofit${state.museumHasGiftShop ? ' (with gift shop)' : ''}`
                : 'Ecommerce store'}
            </span>
          </p>
          <p>Site ID: {state.siteId || '—'}</p>
          <p>Domain: {state.frontendDomain || `${state.siteId}.rumio.world`}</p>
          <p>API: {state.apiDomain || `api.${state.siteId}.rumio.world`}</p>
        </SummaryCard>

        {/* Branding */}
        <SummaryCard
          icon={Palette}
          title="Branding"
          onEdit={() => updateState({ currentStep: 1 })}
        >
          {primaryShades.length > 0 ? (
            <div className="flex items-center gap-1 flex-wrap">
              {primaryShades.map((c, i) => (
                <ColorSwatch key={i} color={c} />
              ))}
              {accentColor && (
                <>
                  <span className="text-xs text-text-muted mx-1">+</span>
                  <ColorSwatch color={accentColor} label="accent" />
                </>
              )}
            </div>
          ) : (
            <p>No palette configured</p>
          )}
          <p>Font: {state.fontPairingId || '—'}</p>
        </SummaryCard>

        {/* Features */}
        <SummaryCard
          icon={Puzzle}
          title="Features"
          onEdit={() => updateState({ currentStep: 2 })}
        >
          <p className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            {enabledFeatures.length} features enabled
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {topFeatureKeys.map((key) => (
              <span
                key={key}
                className="inline-block px-2 py-0.5 bg-stone-100 text-xs rounded-full text-text-secondary"
              >
                {featureLabels[key] || key}
              </span>
            ))}
            {enabledFeatures.length > 5 && (
              <span className="inline-block px-2 py-0.5 text-xs text-text-muted">
                +{enabledFeatures.length - 5} more
              </span>
            )}
          </div>
        </SummaryCard>

        {/* Content */}
        <SummaryCard
          icon={FileText}
          title="Content"
          onEdit={() => updateState({ currentStep: 3 })}
        >
          <p className="truncate">
            Hero: {state.heroTitle || <span className="text-text-muted italic">Not set</span>}
          </p>
          <p>Value props: {state.valuePropositions?.length || 0}</p>
          <p>
            About page:{' '}
            {state.aboutMission ? (
              <span className="text-green-600 font-medium">Ready</span>
            ) : (
              <span className="text-text-muted">Not started</span>
            )}
          </p>
        </SummaryCard>

        {/* Products / Courses */}
        <SummaryCard
          icon={isCourse ? GraduationCap : ShoppingBag}
          title={
            isCourse
              ? 'Courses & Content'
              : isSaas
                ? 'Marketing Content'
                : isMuseum
                  ? 'Museum Content'
                  : isLodging
                    ? 'Listings & Content'
                    : 'Products & Content'
          }
          onEdit={() => updateState({ currentStep: 4 })}
        >
          {isCourse ? (
            <p>{courseCount} course{courseCount !== 1 ? 's' : ''} planned</p>
          ) : isDropship ? (
            <p className="text-blue-600 font-medium">Dippy will source real products</p>
          ) : isSaas ? (
            <p className="text-text-muted">Marketing site — no product catalog</p>
          ) : isMuseum ? (
            <p className="text-text-muted">Events &amp; memberships — no product catalog</p>
          ) : isLodging ? (
            <p className="text-text-muted">Properties &amp; reservations — no product catalog</p>
          ) : (
            <p>{state.productCount} sample product{state.productCount !== 1 ? 's' : ''}</p>
          )}
          <p>{state.blogPostCount} seed blog post{state.blogPostCount !== 1 ? 's' : ''}</p>
          <p className="text-text-muted">Blog Agent: auto-publishes 2x/week</p>
          {state.generateContent && (
            <p className="text-text-muted">
              AI generates:{' '}
              {isCourse
                ? 'courses, blog, pages, images'
                : isDropship
                  ? 'categories, blog, pages, images'
                  : hasProducts
                    ? 'products, categories, blog, pages, images'
                    : 'blog, pages, images'}
            </p>
          )}
        </SummaryCard>
      </div>

      {/* ── Auto-configured Agents ── */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-text-primary">Auto-configured for you</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="flex items-start gap-2.5 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Bot className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-green-800">Blog Agent</p>
              <p className="text-xs text-green-700">Publishes 2x/week (Tue & Fri)</p>
            </div>
          </div>
          {isDropship && (
            <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Truck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-blue-800">Dippy Agent</p>
                <p className="text-xs text-blue-700">Sources & manages products</p>
              </div>
            </div>
          )}
          {state.setupAxiss && (
            <div className="flex items-start gap-2.5 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <Headset className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-purple-800">Axiss Support</p>
                <p className="text-xs text-purple-700">AI widget + 20 FAQs + 10 KB articles</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Payment & Services ── */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Payment & Services
        </h2>

        <div className="border border-stone-200 rounded-lg p-4 bg-white space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PasswordField
              label="Stripe Secret Key"
              value={state.stripeSecretKey}
              onChange={(v) => updateState({ stripeSecretKey: v })}
              placeholder="sk_live_..."
            />
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">
                Stripe Publishable Key
              </label>
              <input
                type="text"
                value={state.stripePublishableKey}
                onChange={(e) => updateState({ stripePublishableKey: e.target.value })}
                placeholder="pk_live_..."
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <PasswordField
              label="Stripe Webhook Secret"
              value={state.stripeWebhookSecret}
              onChange={(v) => updateState({ stripeWebhookSecret: v })}
              placeholder="whsec_..."
            />
          </div>

          <div className="flex items-start gap-2 text-xs text-text-muted">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>You can add these later from the admin settings.</p>
          </div>

          {/* Admin users summary — full editor moved to Step 1.
              Show a read-only list with an "Edit" link back to Step 1 so the
              reviewer can verify the customer's email is in there before
              creating the site. */}
          <div className="border-t border-stone-100 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-text-primary">Admin Users</h4>
              <button
                type="button"
                onClick={() => updateState({ currentStep: 0 })}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Edit in Step 1 →
              </button>
            </div>
            <p className="text-xs text-text-muted">
              {user?.email
                ? `Platform owner: ${user.email}`
                : 'Platform owner: (your account)'}
            </p>
            {(state.additionalAdmins || []).filter((e) => e && e.includes('@')).length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                ⚠ No site admins added. If you're building this for a customer, go back to Step 1 and add their email.
              </p>
            ) : (
              <ul className="text-xs text-text-secondary space-y-1">
                {(state.additionalAdmins || [])
                  .filter((e) => e && e.includes('@'))
                  .map((email) => (
                    <li key={email} className="flex items-center gap-2">
                      <span className="text-text-muted">•</span>
                      <span className="font-mono">{email}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          <div className="border-t border-stone-100 pt-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={state.setupAxiss}
                onChange={(e) => updateState({ setupAxiss: e.target.checked })}
                className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-text-primary">Setup Axiss support widget</span>
            </label>

            {/* Nested under Axiss: a phone line answers for an Axiss tenant,
                so there is nothing to provision without one. Unchecking Axiss
                takes voice with it rather than leaving an orphan flag set. */}
            {state.setupAxiss && (
              <div className="ml-7 space-y-3 border-l border-stone-100 pl-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.setupVoice}
                    onChange={(e) => updateState({ setupVoice: e.target.checked })}
                    className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-text-primary">
                    Add AI phone support (provisions a number)
                  </span>
                </label>

                {state.setupVoice && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-4">
                      {([
                        { value: 'toll_free', label: 'Toll-free' },
                        { value: 'local', label: 'Local' },
                      ] as const).map((opt) => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="voiceNumberType"
                            checked={state.voiceNumberType === opt.value}
                            onChange={() => updateState({ voiceNumberType: opt.value })}
                            className="h-4 w-4 border-stone-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-text-primary">{opt.label}</span>
                        </label>
                      ))}
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={state.voiceAreaCode}
                        onChange={(e) =>
                          updateState({ voiceAreaCode: e.target.value.replace(/[^0-9]/g, '') })
                        }
                        placeholder="Area code"
                        aria-label="Preferred area code"
                        className="w-28 rounded-md border border-stone-300 px-2 py-1 text-sm"
                      />
                    </div>
                    <p className="text-xs text-text-muted">
                      The number answers calls as soon as the site is live. Texting is separate:
                      a {state.voiceNumberType === 'toll_free' ? 'toll-free' : 'local'} number
                      cannot send until{' '}
                      {state.voiceNumberType === 'toll_free'
                        ? 'toll-free verification'
                        : 'A2P 10DLC registration'}{' '}
                      clears, which takes days to weeks and needs the site to be publicly live
                      first. Area code is a preference, not a guarantee.
                    </p>
                  </div>
                )}
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={state.setupTaxEngine}
                onChange={(e) => updateState({ setupTaxEngine: e.target.checked })}
                className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-text-primary">Setup Tax Engine</span>
            </label>
          </div>
        </div>
      </section>

      {/* ── Post-Creation Setup Instructions ── */}
      <section className="space-y-4">
        <h3 className="text-base font-semibold text-text-primary">After site creation, you'll need to:</h3>

        {/* Social login — nothing to do. Every provider redirects to the
            platform relay's fixed URI, so a new tenant domain never needs
            registering in the Google/Apple/Meta consoles. */}
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800 space-y-2">
          <p className="font-semibold">Social login — Auto-configured!</p>
          <p className="text-green-700">
            Google, Apple and Facebook sign-in work on this site as soon as it deploys — no OAuth
            console changes needed. Pick which buttons to show in Admin → Settings → Social Login.
          </p>
        </div>

        {/* Cloudflare Turnstile */}
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800 space-y-2">
          <p className="font-semibold">Cloudflare Turnstile — Auto-configured!</p>
          <p className="text-green-700">
            A new Turnstile widget will be created automatically for your site during setup.
            No manual configuration needed.
          </p>
        </div>

        {/* Stripe Webhook */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-sm text-purple-800 space-y-2">
          <p className="font-semibold">Stripe Webhooks — Register endpoint</p>
          <p className="text-purple-700">
            Go to{' '}
            <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="underline font-medium">
              Stripe Dashboard → Webhooks
            </a>{' '}
            and add a new endpoint:
          </p>
          <div className="flex items-center gap-2 ml-2">
            <span className="text-purple-600 text-xs font-medium w-36">Endpoint URL:</span>
            <code className="bg-purple-100 px-2 py-0.5 rounded font-mono text-xs">{`https://${state.apiDomain}/webhooks/stripe`}</code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(`https://${state.apiDomain}/webhooks/stripe`);
                setCopiedKey('webhook');
                setTimeout(() => setCopiedKey(null), 2000);
              }}
              className="p-0.5 rounded hover:bg-purple-200 text-purple-600 transition-colors"
              title="Copy"
            >
              {copiedKey === 'webhook' ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
      </section>

      {/* ── Create Site ── */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-center pt-2">
        <Button
          variant="boutique"
          size="lg"
          onClick={handleCreateSite}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          className="px-10 py-3 text-base"
        >
          <Rocket className="w-5 h-5" />
          Create My Site
        </Button>
      </div>
    </div>
  );
}
